import { Injectable } from '@angular/core';
import { PurchaseDataProvider } from '../tokens';
import { OfflineStateService } from './offline-state.service';
import { OfflineSequenceService } from './offline-sequence.service';
import type {
  PurchaseOrderFilterParams, OfflinePurchaseOrder,
  CreatePurchaseOrderData, ReceivePurchaseOrderData, SuccessResult,
} from '../types/offline-models';

@Injectable({ providedIn: 'root' })
export class OfflinePurchaseService implements PurchaseDataProvider {
  constructor(
    private offlineState: OfflineStateService,
    private sequenceService: OfflineSequenceService,
  ) {}

  async getPurchaseOrders(params: PurchaseOrderFilterParams): Promise<OfflinePurchaseOrder[]> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');
    const selector: Record<string, string> = {};
    if (params?.status) selector['status'] = params.status;
    const docs = await db.purchase_orders.find({ selector }).exec();
    return docs.map(d => d.toJSON() as OfflinePurchaseOrder);
  }

  async createPurchaseOrder(data: CreatePurchaseOrderData): Promise<SuccessResult> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');

    const orderNumber = await this.sequenceService.generatePurchaseOrderNumber(data.userId);
    const now = new Date().toISOString();

    let totalAmount = 0;
    const orderLines = data.lines.map(line => {
      const totalPrice = line.quantity * line.unitPrice;
      totalAmount += totalPrice;
      return { ...line, totalPrice };
    });

    await db.purchase_orders.insert({
      id: String(Date.now()),
      orderNumber,
      supplierId: data.supplierId,
      orderDate: now,
      expectedDeliveryDate: null,
      status: 'DRAFT',
      totalAmount,
      isReceived: false,
      currency: null,
      notes: null,
      lines: orderLines,
      companyId: data.companyId,
      offlineId: crypto.randomUUID(),
      syncStatus: 'PENDING',
      createdAt: now,
      updatedAt: now,
    });

    await this.offlineState.refreshPendingCount();
    return { success: true, orderNumber };
  }

  async receivePurchaseOrder(id: number, data: ReceivePurchaseOrderData): Promise<SuccessResult> {
    const db = this.offlineState.getDb();
    if (!db) throw new Error('Offline DB not initialized');

    const po = await db.purchase_orders.findOne(String(id)).exec();
    if (!po) throw new Error(`Purchase order ${id} not found`);

    await po.incrementalPatch({
      status: 'RECEIVED',
      isReceived: true,
      updatedAt: new Date().toISOString(),
    });

    const poData = po.toJSON() as OfflinePurchaseOrder;

    for (const line of poData.lines) {
      await db.stock_movements.insert({
        id: crypto.randomUUID(),
        productId: line.productId,
        fromWarehouseId: null,
        toWarehouseId: data.warehouseId,
        quantity: line.quantity,
        variantId: null,
        batchNumber: null,
        type: 'IN',
        reference: `PO-RCV-${id}`,
        notes: null,
        unitId: null,
        companyId: poData.companyId,
        offlineId: crypto.randomUUID(),
        syncStatus: 'PENDING',
        createdAt: new Date().toISOString(),
        createdBy: data.userId,
      });
    }

    await this.offlineState.refreshPendingCount();
    return { success: true };
  }
}
